/**
 * RunHistoryPanel — slide-out side drawer (right edge) showing recent
 * content generation runs for the active client.
 *
 * Visual model: each run = a card showing started date/time, finished time,
 * status pill (completed/failed/running/cancelled), and a "N/M pages" stat.
 * Hub/parent stat slots are reserved for future content types but currently
 * show "0/0" since we only track flat page counts.
 *
 * Behavior:
 *  - Slides in from the right when `open` is true.
 *  - Backdrop fades in behind it; clicking the backdrop closes the panel.
 *  - ESC also closes.
 *  - Auto-refreshes runs every 5s while open AND the most recent run is still 'running'.
 */
import { useEffect, useRef, useState } from "react";
import type { ContentRun } from "../lib/api";
import { listContentRuns } from "../lib/api";

interface RunHistoryPanelProps {
  clientId: number;
  open: boolean;
  onClose: () => void;
}

export function RunHistoryPanel({ clientId, open, onClose }: RunHistoryPanelProps) {
  const [runs, setRuns] = useState<ContentRun[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const refreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Load runs on open + when clientId changes
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    async function load() {
      try {
        setLoading(true);
        setError(null);
        const data = await listContentRuns(clientId);
        if (!cancelled) setRuns(data);
      } catch (err) {
        if (!cancelled) {
          const msg = err instanceof Error ? err.message : "Failed to load run history";
          setError(msg);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [open, clientId]);

  // Auto-refresh while a run is active
  useEffect(() => {
    if (!open) return;
    const hasActiveRun = runs.some((r) => r.status === "running");
    if (!hasActiveRun) return;
    refreshTimerRef.current = setInterval(async () => {
      try {
        const data = await listContentRuns(clientId);
        setRuns(data);
      } catch {
        // swallow; next tick may succeed
      }
    }, 5000);
    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [open, runs, clientId]);

  // ESC to close
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      {/* Backdrop — clickable area to dismiss */}
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40 transition-opacity duration-200"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Slide-over panel */}
      <aside
        role="dialog"
        aria-label="Run History"
        className="fixed top-0 right-0 bottom-0 w-full max-w-md bg-bg-card border-l border-border shadow-2xl z-50 flex flex-col animate-slide-in-right"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h2 className="text-text font-semibold text-base">Run History</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-text-subtle hover:text-text text-xl leading-none"
            aria-label="Close run history"
          >
            ×
          </button>
        </div>

        {/* Body — scrollable list of run cards */}
        <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2">
          {loading && runs.length === 0 && (
            <div className="text-text-muted text-sm text-center py-8">
              Loading run history…
            </div>
          )}

          {error && (
            <div className="bg-danger-bg border border-danger-border text-danger text-xs rounded-lg p-3">
              {error}
            </div>
          )}

          {!loading && !error && runs.length === 0 && (
            <div className="text-text-muted text-sm text-center py-8">
              No content runs yet for this client.
            </div>
          )}

          {runs.map((run) => (
            <RunCard key={run.id} run={run} />
          ))}
        </div>
      </aside>
    </>
  );
}

function RunCard({ run }: { run: ContentRun }) {
  const startedDate = new Date(run.started_at);
  const finishedDate = run.finished_at ? new Date(run.finished_at) : null;
  const isOutline = run.run_type === "outline";

  // "5/5/2026 03:17 PM"
  const startedDisplay = formatStartedDate(startedDate);
  // "Finished 03:37 PM" (just time-of-day)
  const finishedDisplay = finishedDate
    ? `Finished ${formatTimeOnly(finishedDate)}`
    : run.status === "running"
    ? "In progress…"
    : "—";

  return (
    <div className="bg-bg-subtle border border-border rounded-lg px-4 py-3">
      {/* Top row — type + started/finished on left, status pill on right */}
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span
              className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${
                isOutline
                  ? "bg-accent-subtle text-accent border border-accent/30"
                  : "bg-purple-500/10 text-purple-400 border border-purple-500/30"
              }`}
            >
              {isOutline ? "Outlines" : "Articles"}
            </span>
            <div className="text-text font-semibold text-sm">{startedDisplay}</div>
          </div>
          <div className="text-text-muted text-xs mt-0.5">{finishedDisplay}</div>
        </div>
        <StatusPill status={run.status} />
      </div>

      {/* Stats row */}
      <div className="flex items-center justify-between text-xs pt-1">
        {isOutline ? (
          <>
            <Stat
              label="outlines"
              numerator={run.completed_articles}
              denominator={run.total_articles}
              color="text-accent"
            />
            <Stat
              label="failed"
              numerator={run.failed_articles}
              denominator={run.total_articles}
              color={run.failed_articles > 0 ? "text-danger" : "text-text-muted"}
            />
          </>
        ) : (
          <>
            <Stat
              label="pages"
              numerator={run.completed_articles}
              denominator={run.total_articles}
              color="text-accent"
            />
            <Stat label="hub" numerator={0} denominator={0} color="text-purple-400" />
            <Stat label="parent" numerator={0} denominator={0} color="text-purple-400" />
          </>
        )}
      </div>
    </div>
  );
}

function Stat({
  label,
  numerator,
  denominator,
  color,
}: {
  label: string;
  numerator: number;
  denominator: number;
  color: string;
}) {
  return (
    <div className="flex items-baseline gap-1">
      <span className={`font-semibold ${color}`}>{numerator}</span>
      <span className="text-text-muted">/{denominator}</span>
      <span className="text-text-muted ml-1">{label}</span>
    </div>
  );
}

function StatusPill({ status }: { status: ContentRun["status"] }) {
  const config: Record<
    ContentRun["status"],
    { label: string; classes: string }
  > = {
    completed: {
      label: "completed",
      classes: "bg-success-bg text-success border-success-border",
    },
    failed: {
      label: "failed",
      classes: "bg-danger-bg text-danger border-danger-border",
    },
    running: {
      label: "running",
      classes: "bg-accent-subtle text-accent border-accent/30",
    },
    cancelled: {
      label: "cancelled",
      classes: "bg-bg-subtle text-text-muted border-border",
    },
  };
  const c = config[status];
  return (
    <span
      className={`text-[11px] uppercase font-medium px-2 py-0.5 rounded border whitespace-nowrap ${c.classes}`}
    >
      {c.label}
    </span>
  );
}

// "5/5/2026 03:17 PM"
function formatStartedDate(d: Date): string {
  const month = d.getMonth() + 1;
  const day = d.getDate();
  const year = d.getFullYear();
  return `${month}/${day}/${year} ${formatTimeOnly(d)}`;
}

// "03:17 PM"
function formatTimeOnly(d: Date): string {
  let hours = d.getHours();
  const minutes = d.getMinutes().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  if (hours === 0) hours = 12;
  return `${hours.toString().padStart(2, "0")}:${minutes} ${ampm}`;
}
